// Risk Dashboard Component
// Dashboard for risk management overview and metrics

import React, { useState, useEffect } from 'react';
import { RiskDashboard as RiskDashboardType } from '../../types/risk';
import { RiskService } from '../../services/riskService';
import { useLanguage } from '../../state/language/LanguageProvider';

interface RiskDashboardProps {
  onDashboardData?: (data: RiskDashboardType | null) => void;
}

export const RiskDashboard: React.FC<RiskDashboardProps> = ({ onDashboardData }) => {
  const { t } = useLanguage();
  const [dashboard, setDashboard] = useState<RiskDashboardType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const riskService = new RiskService();

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const dashboardData = await riskService.getRiskDashboard();
      setDashboard(dashboardData);
      onDashboardData?.(dashboardData);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('risk.dashboard.errors.loadDashboard'));
      onDashboardData?.(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="text-center py-8">
        <p className="text-[var(--color-secondary-text)]">{t('risk.dashboard.noData')}</p>
      </div>
    );
  }

  return (
    <div className="risk-dashboard">
      <div className="grid grid-cols-4 gap-2 sm:gap-4 md:gap-6 mb-8">
        {/* Summary Cards */}
        <div className="border border-[var(--color-border)] rounded-lg p-2 sm:p-4 md:p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-500 rounded-md flex items-center justify-center">
                <svg className="w-3 h-3 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-2 sm:ml-5 w-0 flex-1 min-w-0">
              <dl>
                <dt className="text-xs sm:text-sm font-medium text-[var(--color-secondary-text)] truncate">{t('risk.dashboard.totalRisks')}</dt>
                <dd className="text-sm sm:text-lg text-[var(--color-surface-text)] truncate">{dashboard?.summary?.total_risks || 0}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="border border-[var(--color-border)] rounded-lg p-2 sm:p-4 md:p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                <svg className="w-3 h-3 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>
            <div className="ml-2 sm:ml-5 w-0 flex-1 min-w-0">
              <dl>
                <dt className="text-xs sm:text-sm font-medium text-[var(--color-secondary-text)] truncate">{t('risk.dashboard.unidentified') === 'Unidentified' ? 'Identified' : (t('risk.dashboard.unidentified') || 'Identified')}</dt>
                <dd className="text-sm sm:text-lg text-[var(--color-surface-text)] truncate">{dashboard?.summary?.unidentified || 0}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="border border-[var(--color-border)] rounded-lg p-2 sm:p-4 md:p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-500 rounded-md flex items-center justify-center">
                <svg className="w-3 h-3 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-2 sm:ml-5 w-0 flex-1 min-w-0">
              <dl>
                <dt className="text-xs sm:text-sm font-medium text-[var(--color-secondary-text)] truncate">{t('risk.dashboard.assigned')}</dt>
                <dd className="text-sm sm:text-lg text-[var(--color-surface-text)] truncate">{dashboard?.summary?.assigned || 0}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="border border-[var(--color-border)] rounded-lg p-2 sm:p-4 md:p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-red-500 rounded-md flex items-center justify-center">
                <svg className="w-3 h-3 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>
            <div className="ml-2 sm:ml-5 w-0 flex-1 min-w-0">
              <dl>
                <dt className="text-xs sm:text-sm font-medium text-[var(--color-secondary-text)] truncate">{t('risk.dashboard.escalated')}</dt>
                <dd className="text-sm sm:text-lg text-[var(--color-surface-text)] truncate">{dashboard?.summary?.escalated || 0}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Summary cards only - Recent Activity and Deadlines moved to right side */}
    </div>
  );
};
